import React, { useEffect, useState } from 'react';
import {
  Typography, Box, Chip, LinearProgress,
  CircularProgress, Alert, Divider, Button, List, ListItem, ListItemButton,
  ListItemText, ListItemIcon, Paper, Container,
} from '@mui/material';
import { QRCodeSVG } from 'qrcode.react';
import {
  CheckCircle, PlayCircle, TextFields, VideoLibrary, DashboardCustomize,
  Quiz, EmojiEvents, Lock, ArrowBack, Print, Replay, Star,
} from '@mui/icons-material';
import { useParams, useNavigate } from 'react-router-dom';
import { learnerAPI } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { FullCourse, CourseModule, CourseTest, TestAttemptResult } from '../types';
import TestPlayer from '../components/tests/TestPlayer';

type ViewState =
  | { kind: 'module'; module: CourseModule }
  | { kind: 'test'; test: CourseTest }
  | { kind: 'modules-complete' }
  | { kind: 'ready-for-final' }
  | { kind: 'certificate' }
  | { kind: 'exam-failed' }
  | { kind: 'none' };

// ── Helpers ────────────────────────────────────────────────────────────────

const API_ORIGIN = (process.env.REACT_APP_API_URL || 'http://localhost:8080/api').replace(/\/api$/, '');

function toAbsUrl(url: string): string {
  if (!url) return '';
  if (url.startsWith('http://') || url.startsWith('https://')) return url;
  return `${API_ORIGIN}${url}`;
}

const BRAND_GRADIENT = 'linear-gradient(to right, #00CCCC, #FFC840, #FFB000, #CD00FF, #DD50FF, #00B2AD)';

// ── Certificate persistence (localStorage) ────────────────────────────────

interface SavedCert {
  percentage: number;
  date: string;
  code: string;
  learnerName: string;
  courseName: string;
}

// 10-char alphanumeric code (no O/0/I/1 to avoid confusion)
const genVerifyCode = (): string => {
  const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
  let c = '';
  for (let i = 0; i < 10; i++) c += chars[Math.floor(Math.random() * chars.length)];
  return c;
};

const certKey   = (courseId: string) => `sedstart_cert_${courseId}`;
const loadCert  = (courseId: string): SavedCert | null => {
  try { const v = localStorage.getItem(certKey(courseId)); return v ? JSON.parse(v) : null; }
  catch { return null; }
};
const persistCert = (courseId: string, data: SavedCert) => {
  try {
    localStorage.setItem(certKey(courseId), JSON.stringify(data));
    // Store in global verify index so /verify/:code can look it up
    const idx = JSON.parse(localStorage.getItem('sedstart_certs_index') || '{}');
    idx[data.code] = { learnerName: data.learnerName, courseName: data.courseName, date: data.date };
    localStorage.setItem('sedstart_certs_index', JSON.stringify(idx));
  } catch {}
};

// ── Actual Sedstart SVG Logo ───────────────────────────────────────────────

const SedstartLogo: React.FC<{ width?: number }> = ({ width = 140 }) => {
  const h = Math.round((24 / 117) * width);
  return (
    <svg width={width} height={h} viewBox="0 0 117 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <g clipPath="url(#c_cert)">
        <mask id="m_cert" style={{ maskType: 'luminance' as any }} maskUnits="userSpaceOnUse" x="0" y="0" width="22" height="24">
          <path d="M8.266 0.608L20.324 7.382C21.008 7.769 21.435 8.488 21.435 9.235V14.737C21.435 15.511 21.008 16.23 20.324 16.59L8.266 23.336C7.582 23.723 6.727 23.723 6.043 23.336L1.112 20.571C0.428 20.24 0 19.521 0 18.746V5.226C0 4.452 0.428 3.76 1.112 3.373L6.043 0.608C6.727 0.221 7.582 0.221 8.266 0.608Z" fill="white"/>
        </mask>
        <g mask="url(#m_cert)">
          <path d="M7.155 24L0 19.991V12L7.155 16.009V24Z" fill="#00CCCC"/>
          <path d="M14.281 12L7.155 16.009V24L14.281 19.991V12Z" fill="#00B2AD"/>
          <path d="M7.155 16.009L0 12V3.981L7.155 7.991V16.009Z" fill="#FFC840"/>
          <path d="M7.155 7.991L14.281 4.009L7.155 0L0 4.009L7.155 7.991Z" fill="#FFB000"/>
          <path d="M21.436 7.991L14.281 12V19.991L21.436 15.982V7.991Z" fill="#CD00FF"/>
          <path d="M14.281 12L21.436 7.991L14.281 4.009L7.155 7.991L14.281 12Z" fill="#DD50FF"/>
        </g>
        <path d="M35.4 15.208C35.858 16.59 36.97 17.53 39.592 17.53C42.215 17.53 42.927 16.701 42.927 15.65C42.927 14.599 42.186 13.853 38.823 13.3C33.892 12.47 32.894 10.949 32.894 8.986C32.894 6.221 35.63 4.756 39.051 4.756C44.067 4.756 45.379 7.272 45.55 8.876H42.414C42.186 8.074 41.645 6.995 38.965 6.995C36.286 6.995 36.086 7.576 36.086 8.682C36.086 9.788 36.685 10.148 39.678 10.673C45.236 11.613 46.12 13.272 46.12 15.346C46.12 17.42 44.124 19.797 39.307 19.797C34.49 19.797 32.723 17.834 32.238 15.208H35.4Z" fill="black"/>
        <path d="M50.053 14.931C50.139 16.424 51.25 17.585 53.046 17.585C54.842 17.585 55.184 17.115 55.64 16.341H58.633C58.006 18.111 56.41 19.77 53.018 19.77C49.626 19.77 47.117 16.949 47.117 14.267C47.117 11.585 49.198 8.71 52.989 8.71C56.78 8.71 58.775 11.558 58.775 14.24C58.775 14.931 58.775 14.654 58.775 14.959H50.11L50.053 14.931ZM55.697 13.106C55.611 12.166 55.098 10.783 52.932 10.783C50.766 10.783 50.224 11.862 50.082 13.106H55.697Z" fill="black"/>
        <path d="M71.659 4.23V16.507C71.659 17.502 71.659 18.498 71.659 19.493H68.723C68.666 19.134 68.666 18.608 68.638 18.166C68.068 18.995 67.241 19.797 64.932 19.797C62.623 19.797 59.688 17.613 59.688 14.35C59.688 11.088 62.509 8.737 65.246 8.737C67.982 8.737 68.353 9.567 68.666 9.982V4.23H71.659ZM62.766 14.267C62.766 16.009 63.821 17.475 65.759 17.475C67.697 17.475 68.752 16.203 68.752 14.24C68.752 12.277 67.982 11.005 65.816 11.005C63.65 11.005 62.794 12.608 62.794 14.295L62.766 14.267Z" fill="black"/>
        <path d="M75.592 16.286C75.906 17.198 76.733 17.751 78.357 17.751C79.982 17.751 80.524 17.226 80.524 16.59C80.524 15.954 80.039 15.539 77.673 15.235C73.654 14.71 73.027 13.3 73.027 12C73.027 10.701 74.224 8.71 77.901 8.71C81.578 8.71 82.946 10.507 83.089 11.917H80.125C79.982 11.392 79.554 10.673 77.759 10.673C75.963 10.673 75.963 11.254 75.963 11.696C75.963 12.138 76.362 12.581 78.528 12.857C82.804 13.466 83.459 14.82 83.459 16.313C83.459 17.807 81.977 19.742 78.158 19.742C74.338 19.742 72.97 18.498 72.628 16.258H75.592V16.286Z" fill="black"/>
        <path d="M84.429 8.986V5.806H87.422V8.986H90.016V11.115H87.422V15.954C87.422 16.949 87.65 17.392 88.733 17.392C89.816 17.392 89.389 17.392 89.702 17.309V19.327C89.161 19.493 88.419 19.548 87.678 19.548C85.142 19.548 84.4 18.47 84.4 16.424V11.115H84.429V8.986Z" fill="black"/>
        <path d="M111.051 8.986V5.806H114.044V8.986H116.638V11.115H114.044V15.954C114.044 16.949 114.272 17.392 115.356 17.392C116.439 17.392 116.011 17.392 116.325 17.309V19.327C115.783 19.493 115.042 19.548 114.301 19.548C111.764 19.548 111.023 18.47 111.023 16.424V11.115L112.477 9.263C112.562 9.152 112.477 8.986 112.334 8.986H111.023H111.051Z" fill="black"/>
        <path d="M101.759 16.645C101.759 17.89 101.816 19.244 101.845 19.466H99.023C98.937 19.161 98.909 18.47 98.909 18.166C98.339 19.134 97.084 19.742 94.975 19.742C92.866 19.742 90.814 17.973 90.814 16.341C90.814 13.604 93.322 12.857 96.457 12.857H98.852V12.443C98.852 11.502 98.424 10.701 96.628 10.701C94.833 10.701 94.576 11.337 94.434 12.055H91.441C91.555 10.728 92.495 8.682 96.6 8.682C100.704 8.682 101.787 10.728 101.787 12.498V16.645H101.759ZM98.852 14.765C98.567 14.765 97.825 14.765 96.514 14.765C94.548 14.765 93.692 15.235 93.692 16.258C93.692 17.281 94.32 17.724 95.745 17.724C97.94 17.724 98.823 16.645 98.823 15.069V14.82L98.852 14.765Z" fill="black"/>
        <path d="M102.842 11.834C102.842 10.894 102.842 9.926 102.842 8.986H105.75C105.807 9.346 105.835 10.479 105.835 11.171C106.462 9.705 107.859 8.682 110.082 8.71V11.364C107.631 11.337 105.835 12.443 105.835 15.456V19.466H102.842V11.834Z" fill="black"/>
      </g>
      <defs>
        <clipPath id="c_cert">
          <rect width="116.667" height="24" fill="white"/>
        </clipPath>
      </defs>
    </svg>
  );
};

// ── ContentViewer ──────────────────────────────────────────────────────────

const ContentViewer: React.FC<{ module: CourseModule }> = ({ module }) => {
  const c = module.content || {};

  if (module.content_type === 'text') {
    return (
      <Box
        sx={{
          '& h2': { mt: 0, mb: 1.5, fontSize: '1.4rem', fontWeight: 600 },
          '& h3': { mt: 2, mb: 1, fontSize: '1.1rem', fontWeight: 600 },
          '& p': { lineHeight: 1.8, color: 'text.secondary' },
          '& ul, & ol': { pl: 3, '& li': { mb: 0.5, color: 'text.secondary' } },
          '& pre': { bgcolor: '#f5f5f5', p: 2, borderRadius: 1, overflow: 'auto', fontSize: 13 },
          '& code': { bgcolor: '#f5f5f5', px: 0.5, borderRadius: 0.5, fontFamily: 'monospace', fontSize: 13 },
          '& a': { color: 'primary.main' },
          '& strong': { fontWeight: 600, color: 'text.primary' },
        }}
        dangerouslySetInnerHTML={{ __html: c.html || '<p>No content yet.</p>' }}
      />
    );
  }

  if (module.content_type === 'video') {
    const isUpload = c.url && (
      c.url.startsWith('/uploads/') || c.url.includes('/uploads/') ||
      /\.(mp4|webm|ogg|mov)(\?|$)/i.test(c.url)
    );
    return (
      <Box>
        {c.description && (
          <Typography variant="body2" color="text.secondary" paragraph>{c.description}</Typography>
        )}
        <Paper variant="outlined" sx={{ overflow: 'hidden', borderRadius: 1 }}>
          {isUpload ? (
            <video src={toAbsUrl(c.url)} controls style={{ width: '100%', display: 'block' }} />
          ) : (
            <Box sx={{ position: 'relative', paddingTop: '56.25%' }}>
              <iframe
                src={c.url}
                style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', border: 'none' }}
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                title={module.title}
              />
            </Box>
          )}
        </Paper>
      </Box>
    );
  }

  if (module.content_type === 'multimedia') {
    const embedSrc = c.embed_url || '';
    const isUpload = embedSrc.startsWith('/uploads/') || embedSrc.includes('/uploads/');
    const absUrl   = toAbsUrl(embedSrc);
    const isImage  = /\.(jpg|jpeg|png|gif|webp|svg)(\?|$)/i.test(embedSrc);
    const isAudio  = /\.(mp3|wav|ogg|aac|flac)(\?|$)/i.test(embedSrc);
    const isPDF    = /\.pdf(\?|$)/i.test(embedSrc);
    return (
      <Box>
        {c.description && <Alert severity="info" sx={{ mb: 2 }}>{c.description}</Alert>}
        <Paper variant="outlined" sx={{ overflow: 'hidden', borderRadius: 1 }}>
          {isUpload && isImage ? (
            <Box textAlign="center" p={2}>
              <img src={absUrl} alt={module.title} style={{ maxWidth: '100%', borderRadius: 4 }} />
            </Box>
          ) : isUpload && isAudio ? (
            <Box p={2}><audio controls style={{ width: '100%' }}><source src={absUrl} /></audio></Box>
          ) : isUpload && isPDF ? (
            <Box p={1}>
              <embed src={absUrl} type="application/pdf" width="100%" height="600px" style={{ border: 'none', display: 'block' }} />
              <Box textAlign="center" mt={1}>
                <Button href={absUrl} target="_blank" size="small" variant="outlined">Open PDF in new tab</Button>
              </Box>
            </Box>
          ) : (
            <Box sx={{ position: 'relative', paddingTop: '60%' }}>
              <iframe
                src={absUrl}
                style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', border: 'none' }}
                allow="accelerometer; autoplay; clipboard-write; encrypted-media"
                title={module.title}
                sandbox="allow-scripts allow-same-origin allow-forms allow-popups"
              />
            </Box>
          )}
        </Paper>
      </Box>
    );
  }

  return <Typography color="text.secondary">No content available.</Typography>;
};

// ── Module sidebar icon ────────────────────────────────────────────────────

const moduleIcon = (module: CourseModule) => {
  if (module.is_completed) return <CheckCircle color="success" fontSize="small" />;
  if (module.is_locked)    return <Lock color="disabled" fontSize="small" />;
  if (module.content_type === 'video') return <VideoLibrary color="error" fontSize="small" />;
  if (module.content_type === 'multimedia') return <DashboardCustomize color="secondary" fontSize="small" />;
  return <TextFields color="primary" fontSize="small" />;
};

// ── Intermediate screens ───────────────────────────────────────────────────

const ModulesCompleteScreen: React.FC<{
  course: FullCourse;
  onStartTest: () => void;
  onReview: () => void;
}> = ({ course, onStartTest, onReview }) => (
  <Container maxWidth="sm" sx={{ py: 10, textAlign: 'center' }}>
    <Box sx={{ width: 80, height: 80, borderRadius: '50%', bgcolor: 'success.light', display: 'flex', alignItems: 'center', justifyContent: 'center', mx: 'auto', mb: 3 }}>
      <CheckCircle sx={{ fontSize: 48, color: 'success.main' }} />
    </Box>
    <Typography variant="h4" fontWeight={800} gutterBottom>All Modules Completed!</Typography>
    <Typography color="text.secondary" sx={{ mb: 1 }}>
      Excellent work completing <strong>{course.title}</strong>.
    </Typography>
    <Typography color="text.secondary" sx={{ mb: 4 }}>
      Before taking the Final Certification Exam, you must first pass the <strong>Practice Quiz</strong>.
      It's a great way to check your understanding and build confidence.
    </Typography>
    <Box sx={{ background: BRAND_GRADIENT, borderRadius: 2, p: '2px', mb: 4 }}>
      <Box sx={{ bgcolor: '#fff', borderRadius: '6px', p: 3 }}>
        <Quiz sx={{ fontSize: 36, color: '#FFC840', mb: 1 }} />
        <Typography variant="h6" fontWeight={700}>{course.mock_test?.title || 'Practice Quiz'}</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          {course.mock_test?.questions?.length ?? 0} questions · {course.mock_test?.time_limit ?? 15} min · Pass mark {course.mock_test?.passing_score ?? 60}%
        </Typography>
      </Box>
    </Box>
    <Button variant="contained" size="large" startIcon={<Quiz />} onClick={onStartTest}
      sx={{ background: BRAND_GRADIENT, color: '#fff', fontWeight: 700, px: 4, py: 1.5, borderRadius: 3, mr: 2,
        '&:hover': { opacity: 0.9 } }}>
      Start Practice Quiz
    </Button>
    <Button variant="outlined" size="large" onClick={onReview} sx={{ px: 4, py: 1.5, borderRadius: 3 }}>
      Review Modules
    </Button>
  </Container>
);

const ReadyForFinalScreen: React.FC<{
  course: FullCourse;
  mockScore: number;
  onStartFinal: () => void;
  onReview: () => void;
}> = ({ course, mockScore, onStartFinal, onReview }) => (
  <Container maxWidth="sm" sx={{ py: 10, textAlign: 'center' }}>
    <Box sx={{ width: 80, height: 80, borderRadius: '50%', bgcolor: '#FFF3CD', display: 'flex', alignItems: 'center', justifyContent: 'center', mx: 'auto', mb: 3 }}>
      <Star sx={{ fontSize: 48, color: '#FFC840' }} />
    </Box>
    <Typography variant="h4" fontWeight={800} gutterBottom>Practice Quiz Passed!</Typography>
    <Typography color="text.secondary" sx={{ mb: 1 }}>
      You scored <strong>{Math.round(mockScore)}%</strong> on the Practice Quiz.
    </Typography>
    <Typography color="text.secondary" sx={{ mb: 4 }}>
      You're ready for the <strong>Final Certification Exam</strong>.
      Take your time — you need {course.final_test?.passing_score ?? 80}% to earn your certificate.
    </Typography>
    <Box sx={{ background: BRAND_GRADIENT, borderRadius: 2, p: '2px', mb: 4 }}>
      <Box sx={{ bgcolor: '#fff', borderRadius: '6px', p: 3 }}>
        <EmojiEvents sx={{ fontSize: 36, color: '#CD00FF', mb: 1 }} />
        <Typography variant="h6" fontWeight={700}>{course.final_test?.title || 'Final Certification Exam'}</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          {course.final_test?.questions?.length ?? 0} questions · {course.final_test?.time_limit ?? 30} min · Pass mark {course.final_test?.passing_score ?? 80}%
        </Typography>
      </Box>
    </Box>
    <Button variant="contained" size="large" startIcon={<EmojiEvents />} onClick={onStartFinal}
      sx={{ background: BRAND_GRADIENT, color: '#fff', fontWeight: 700, px: 4, py: 1.5, borderRadius: 3, mr: 2,
        '&:hover': { opacity: 0.9 } }}>
      Start Final Exam
    </Button>
    <Button variant="outlined" size="large" onClick={onReview} sx={{ px: 4, py: 1.5, borderRadius: 3 }}>
      Review Modules
    </Button>
  </Container>
);

const ExamFailedScreen: React.FC<{
  course: FullCourse;
  score: number;
  passingScore: number;
  onRetry: () => void;
  onReview: () => void;
}> = ({ course, score, passingScore, onRetry, onReview }) => (
  <Container maxWidth="sm" sx={{ py: 10, textAlign: 'center' }}>
    <Box sx={{ width: 80, height: 80, borderRadius: '50%', bgcolor: '#FFF3F3', display: 'flex', alignItems: 'center', justifyContent: 'center', mx: 'auto', mb: 3 }}>
      <Replay sx={{ fontSize: 48, color: 'error.main' }} />
    </Box>
    <Typography variant="h4" fontWeight={800} gutterBottom>Keep Going!</Typography>
    <Typography color="text.secondary" sx={{ mb: 1 }}>
      You scored <strong>{Math.round(score)}%</strong> — the passing score is <strong>{passingScore}%</strong>.
    </Typography>
    <Typography color="text.secondary" sx={{ mb: 4 }}>
      To retake the Final Exam you must <strong>pass the Practice Quiz again</strong> first.
      Review the course materials, then take the Practice Quiz to unlock the Final Exam.
    </Typography>
    <Button variant="contained" color="primary" size="large" startIcon={<Quiz />} onClick={onRetry}
      sx={{ px: 4, py: 1.5, borderRadius: 3, mr: 2 }}>
      Retake Practice Quiz
    </Button>
    <Button variant="outlined" size="large" onClick={onReview} sx={{ px: 4, py: 1.5, borderRadius: 3 }}>
      Review Modules
    </Button>
  </Container>
);

// ── Certificate ────────────────────────────────────────────────────────────

const Certificate: React.FC<{
  learnerName: string;
  courseName: string;
  issuedDate: string;
  verifyCode: string;
  onBack: () => void;
}> = ({ learnerName, courseName, issuedDate, verifyCode, onBack }) => {
  const verifyUrl = `${window.location.origin}/verify/${verifyCode}`;
  const handlePrint = () => {
    const style = document.createElement('style');
    style.id = '__cert_print_style';
    style.innerHTML = `
      @media print {
        body * { visibility: hidden !important; }
        #sedstart-certificate, #sedstart-certificate * { visibility: visible !important; }
        #sedstart-certificate { position: fixed !important; top: 0 !important; left: 0 !important; width: 100% !important; }
      }
    `;
    document.head.appendChild(style);
    window.print();
    setTimeout(() => { document.getElementById('__cert_print_style')?.remove(); }, 2000);
  };

  return (
    <Container maxWidth="md" sx={{ py: 6 }}>
      {/* Congratulations header */}
      <Box textAlign="center" mb={4}>
        <Box sx={{ width: 90, height: 90, borderRadius: '50%', background: BRAND_GRADIENT, display: 'flex', alignItems: 'center', justifyContent: 'center', mx: 'auto', mb: 2 }}>
          <EmojiEvents sx={{ fontSize: 52, color: '#fff' }} />
        </Box>
        <Typography variant="h3" fontWeight={900} sx={{ background: BRAND_GRADIENT, WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text' }}>
          Congratulations!
        </Typography>
        <Typography color="text.secondary" variant="h6" fontWeight={400} mt={1}>
          You have successfully completed <strong style={{ color: '#111' }}>{courseName}</strong>
        </Typography>
      </Box>

      {/* Action buttons */}
      <Box display="flex" justifyContent="center" gap={2} mb={5}>
        <Button variant="outlined" startIcon={<ArrowBack />} onClick={onBack} sx={{ borderRadius: 3 }}>
          Back to Course
        </Button>
        <Button variant="contained" startIcon={<Print />} onClick={handlePrint}
          sx={{ background: BRAND_GRADIENT, color: '#fff', borderRadius: 3, fontWeight: 700,
            '&:hover': { opacity: 0.9 } }}>
          Print Certificate
        </Button>
      </Box>

      {/* The actual printable certificate */}
      <Box id="sedstart-certificate" sx={{
        border: '3px solid #111',
        borderRadius: 3,
        overflow: 'hidden',
        boxShadow: '0 8px 40px rgba(0,0,0,0.15)',
        bgcolor: '#fff',
        fontFamily: '"Helvetica Neue", Arial, sans-serif',
      }}>
        {/* Top color bar */}
        <Box sx={{ height: 10, background: BRAND_GRADIENT }} />

        {/* Certificate body */}
        <Box sx={{ p: { xs: 3, md: 6 } }}>
          {/* Header row */}
          <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={4}>
            <SedstartLogo width={160} />
            <Box textAlign="right">
              <Typography sx={{ fontSize: 11, fontWeight: 700, letterSpacing: 3, textTransform: 'uppercase', color: '#888', mb: 0.5 }}>
                Certificate of
              </Typography>
              <Typography sx={{ fontSize: 22, fontWeight: 900, color: '#111', lineHeight: 1 }}>
                Completion
              </Typography>
            </Box>
          </Box>

          {/* Divider with brand gradient */}
          <Box sx={{ height: 3, background: BRAND_GRADIENT, borderRadius: 2, mb: 4 }} />

          {/* Certificate body text */}
          <Box textAlign="center" sx={{ py: 2 }}>
            <Typography sx={{ fontSize: 14, color: '#555', letterSpacing: 1, textTransform: 'uppercase', mb: 2 }}>
              This is to certify that
            </Typography>

            {/* Learner name */}
            <Box sx={{ borderBottom: '2px solid #111', display: 'inline-block', px: 4, pb: 1, mb: 3 }}>
              <Typography sx={{ fontSize: { xs: 28, md: 40 }, fontWeight: 900, color: '#111', fontStyle: 'italic', letterSpacing: -1, lineHeight: 1.1 }}>
                {learnerName}
              </Typography>
            </Box>

            <Typography sx={{ fontSize: 14, color: '#555', mb: 2 }}>
              has successfully completed the course
            </Typography>

            {/* Course name */}
            <Typography sx={{ fontSize: { xs: 18, md: 24 }, fontWeight: 800, color: '#111', mb: 1, px: 2 }}>
              {courseName}
            </Typography>

          </Box>

          {/* Footer divider */}
          <Box sx={{ height: 1, bgcolor: '#e0e0e0', my: 3 }} />

          {/* Footer row */}
          <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2}>
            <Box>
              <Typography sx={{ fontSize: 11, fontWeight: 700, letterSpacing: 2, textTransform: 'uppercase', color: '#888' }}>
                Date Issued
              </Typography>
              <Typography sx={{ fontWeight: 700, color: '#111' }}>{issuedDate}</Typography>
            </Box>
            <Box textAlign="center">
              <Box sx={{ height: 2, width: 140, bgcolor: '#111', mb: 0.5 }} />
              <Typography sx={{ fontSize: 11, color: '#888', letterSpacing: 1 }}>Authorized Signature</Typography>
              <Typography sx={{ fontSize: 10, color: '#aaa', mt: 0.25 }}>Sedstart Learning Platform</Typography>
            </Box>
            {/* Verification QR + code */}
            <Box display="flex" alignItems="flex-start" gap={1.5}>
              {/* QR code */}
              <Box sx={{ border: '1px solid #e0e0e0', borderRadius: 1, p: 0.5, bgcolor: '#fff', flexShrink: 0 }}>
                <QRCodeSVG value={verifyUrl} size={72} level="M" />
              </Box>
              {/* Label + code to the right of QR */}
              <Box>
                <Typography sx={{ fontSize: 9, fontWeight: 700, letterSpacing: 1.5, textTransform: 'uppercase', color: '#888', mb: 0.75 }}>
                  Verify Certificate
                </Typography>
                {/* 10-digit code displayed as XXXXX-XXXXX */}
                <Box sx={{
                  bgcolor: '#F5F5F5', border: '1px solid #E0E0E0', borderRadius: 1,
                  px: 1.25, py: 0.75, display: 'inline-block',
                }}>
                  <Typography sx={{
                    fontSize: 15, fontWeight: 800, fontFamily: 'monospace',
                    color: '#111', letterSpacing: 2,
                  }}>
                    {verifyCode ? `${verifyCode.slice(0, 5)}-${verifyCode.slice(5)}` : '—'}
                  </Typography>
                </Box>
                <Typography sx={{ fontSize: 8.5, color: '#aaa', mt: 0.75 }}>
                  Scan QR to verify online
                </Typography>
              </Box>
            </Box>
          </Box>
        </Box>

        {/* Bottom color bar */}
        <Box sx={{ height: 10, background: BRAND_GRADIENT }} />
      </Box>
    </Container>
  );
};

// ── Main CourseDetailPage ──────────────────────────────────────────────────

const CourseDetailPage: React.FC = () => {
  const { courseId }  = useParams<{ courseId: string }>();
  const navigate      = useNavigate();
  const { user }      = useAuth();

  const [course, setCourse]                 = useState<FullCourse | null>(null);
  const [loading, setLoading]               = useState(true);
  const [error, setError]                   = useState<string | null>(null);
  const [view, setView]                     = useState<ViewState>({ kind: 'none' });
  const [completedModules, setCompletedModules] = useState<Set<string>>(new Set());
  const [testResults, setTestResults]       = useState<Record<string, TestAttemptResult>>({});
  const [certDate, setCertDate]             = useState(() =>
    new Date().toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' }));
  const [certCode, setCertCode]             = useState('');

  // Load course + restore persisted certificate on mount
  useEffect(() => {
    if (!courseId) return;

    const trackEnrollment = (id: string, courseTitle: string) => {
      const key = `sedstart_enrolled_${id}`;
      if (!localStorage.getItem(key)) {
        localStorage.setItem(key, JSON.stringify({ courseTitle, startedAt: new Date().toISOString() }));
      }
    };

    const loadCourse = async () => {
      try {
        const [res, progressRes] = await Promise.all([
          learnerAPI.getCourseDetail(courseId),
          learnerAPI.getCourseProgress(courseId).catch(() => null),
        ]);
        const c = res.data.course;
        setCourse(c);
        trackEnrollment(courseId, c.title);

        // Restore completed modules from server
        const restoredCompleted = new Set<string>(
          (progressRes?.data?.completed_modules || []).map(String)
        );
        setCompletedModules(restoredCompleted);

        // Resume at last accessed module, or first module
        if (c.modules.length > 0) {
          const lastModuleId = progressRes?.data?.current_module_id
            ? String(progressRes.data.current_module_id)
            : null;
          const resumeModule = lastModuleId
            ? (c.modules.find(m => m.id === lastModuleId) ?? c.modules[0])
            : c.modules[0];
          setView({ kind: 'module', module: resumeModule });
        }
      } catch {
        setError('Failed to load course. The course may not be available.');
      } finally {
        setLoading(false);
      }
    };

    loadCourse();

    const saved = loadCert(courseId);
    if (saved) {
      setCertDate(saved.date);
      // Back-compat: old certs saved before code was added — generate + re-persist
      const code = saved.code || genVerifyCode();
      if (!saved.code) {
        persistCert(courseId, { ...saved, code, learnerName: saved.learnerName || '', courseName: saved.courseName || '' });
      }
      setCertCode(code);
      setTestResults(prev => ({
        ...prev,
        final: {
          passed: true, percentage: saved.percentage,
          score: 0, total_points: 0, passing_score: 0,
          total_questions: 0, correct_answers: 0, time_spent: 0, results: [],
        },
      }));
    }
  }, [courseId]);

  const markModuleComplete = (moduleId: string) => {
    const next = new Set(Array.from(completedModules).concat(moduleId));
    setCompletedModules(next);
    learnerAPI.updateProgress(parseInt(moduleId) || 0, 0).catch(() => {});

    // Auto-advance when ALL modules are done
    if (course && course.modules.every(m => next.has(m.id))) {
      if (course.mock_test) {
        setView({ kind: 'modules-complete' });
      } else if (course.final_test) {
        setView({ kind: 'ready-for-final' });
      }
    }
  };

  const handleTestFinish = (type: string) => (result: TestAttemptResult) => {
    setTestResults(prev => ({ ...prev, [type]: result }));
    if (type === 'mock') {
      setView(result.passed ? { kind: 'ready-for-final' } : { kind: 'none' });
    } else if (type === 'final') {
      if (result.passed) {
        // Persist so certificate survives re-login
        const date = new Date().toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' });
        const code = genVerifyCode();
        setCertDate(date);
        setCertCode(code);
        persistCert(courseId!, {
          percentage: result.percentage, date, code,
          learnerName: user?.name || 'Learner',
          courseName: course!.title,
        });
        setView({ kind: 'certificate' });
      } else {
        setView({ kind: 'exam-failed' });
      }
    } else {
      setView({ kind: 'none' });
    }
  };

  const allModulesComplete = course ? course.modules.every(m => completedModules.has(m.id)) : false;
  const mockPassed         = testResults['mock']?.passed ?? false;
  const finalPassed        = testResults['final']?.passed ?? false;
  const finalEnabled       = mockPassed || (allModulesComplete && !course?.mock_test);

  const progress = course && course.modules.length > 0
    ? (completedModules.size / course.modules.length) * 100
    : 0;

  if (loading) return <Box display="flex" justifyContent="center" pt={8}><CircularProgress /></Box>;

  if (error || !course) {
    return (
      <Container maxWidth="md" sx={{ mt: 4 }}>
        <Alert severity="error" sx={{ mb: 2 }}>{error || 'Course not found'}</Alert>
        <Button startIcon={<ArrowBack />} onClick={() => navigate('/dashboard')}>Back to Dashboard</Button>
      </Container>
    );
  }

  // ── Full-screen overlay views ──────────────────────────────────────────

  if (view.kind === 'test') {
    return (
      <Container maxWidth="lg" sx={{ py: 2 }}>
        <TestPlayer
          courseId={course.id}
          test={view.test}
          onFinish={handleTestFinish(view.test.type)}
          onCancel={() => setView({ kind: 'none' })}
        />
      </Container>
    );
  }

  if (view.kind === 'modules-complete' && course.mock_test) {
    return (
      <ModulesCompleteScreen
        course={course}
        onStartTest={() => setView({ kind: 'test', test: course.mock_test! })}
        onReview={() => setView({ kind: 'module', module: course.modules[0] })}
      />
    );
  }

  if (view.kind === 'ready-for-final' && course.final_test) {
    return (
      <ReadyForFinalScreen
        course={course}
        mockScore={testResults['mock']?.percentage ?? 100}
        onStartFinal={() => setView({ kind: 'test', test: course.final_test! })}
        onReview={() => setView({ kind: 'module', module: course.modules[0] })}
      />
    );
  }

  if (view.kind === 'exam-failed' && course.final_test) {
    return (
      <ExamFailedScreen
        course={course}
        score={testResults['final']?.percentage ?? 0}
        passingScore={course.final_test.passing_score}
        onRetry={() => {
          // Must pass Practice Quiz again before retaking Final Exam
          setTestResults(prev => { const n = { ...prev }; delete n['mock']; return n; });
          setView({ kind: 'modules-complete' });
        }}
        onReview={() => setView({ kind: 'module', module: course.modules[0] })}
      />
    );
  }

  if (view.kind === 'certificate') {
    return (
      <Certificate
        learnerName={user?.name || 'Learner'}
        courseName={course.title}
        issuedDate={certDate}
        verifyCode={certCode}
        onBack={() => setView({ kind: 'none' })}
      />
    );
  }

  // ── Main two-column layout ─────────────────────────────────────────────

  return (
    <Box sx={{ display: 'flex', height: 'calc(100vh - 64px)' }}>
      {/* Sidebar */}
      <Box sx={{ width: 300, flexShrink: 0, borderRight: '1px solid', borderColor: 'divider', overflow: 'auto' }}>
        <Box sx={{ p: 2, borderBottom: '1px solid', borderColor: 'divider' }}>
          <Button size="small" startIcon={<ArrowBack />} onClick={() => navigate('/dashboard')} sx={{ mb: 1 }}>
            Dashboard
          </Button>
          <Typography variant="subtitle1" fontWeight={700} noWrap>{course.title}</Typography>
          <Box display="flex" gap={1} mt={0.5}>
            <Chip label={course.difficulty} size="small" variant="outlined" />
            <Chip label={course.category}   size="small" variant="outlined" />
          </Box>
          <Box mt={1.5}>
            <Box display="flex" justifyContent="space-between" mb={0.5}>
              <Typography variant="caption" color="text.secondary">Progress</Typography>
              <Typography variant="caption" fontWeight={600}>{Math.round(progress)}%</Typography>
            </Box>
            <LinearProgress variant="determinate" value={progress} sx={{ height: 6, borderRadius: 3 }} />
          </Box>
          {finalPassed && (
            <Button fullWidth size="small" variant="contained" startIcon={<EmojiEvents />}
              onClick={() => setView({ kind: 'certificate' })}
              sx={{ mt: 1.5, background: BRAND_GRADIENT, border: 'none',
                '&:hover': { opacity: 0.9 } }}>
              View / Download Certificate
            </Button>
          )}
        </Box>

        {/* Module list */}
        <List dense disablePadding>
          <Box sx={{ px: 2, pt: 1.5, pb: 0.5 }}>
            <Typography variant="caption" color="text.secondary" fontWeight={600} textTransform="uppercase">
              Modules ({course.modules.length})
            </Typography>
          </Box>

          {course.modules.map(mod => {
            const isComplete = completedModules.has(mod.id);
            const isActive   = view.kind === 'module' && view.module.id === mod.id;
            return (
              <ListItem key={mod.id} disablePadding>
                <ListItemButton selected={isActive} onClick={() => setView({ kind: 'module', module: mod })} sx={{ py: 0.75 }}>
                  <ListItemIcon sx={{ minWidth: 32 }}>
                    {isComplete ? <CheckCircle color="success" fontSize="small" /> : moduleIcon(mod)}
                  </ListItemIcon>
                  <ListItemText
                    primary={mod.title}
                    secondary={`${mod.duration} min`}
                    primaryTypographyProps={{ variant: 'body2', noWrap: true, fontWeight: isActive ? 600 : 400 }}
                    secondaryTypographyProps={{ variant: 'caption' }}
                  />
                </ListItemButton>
              </ListItem>
            );
          })}

          {/* Assessments section */}
          {(course.mock_test || course.final_test) && (
            <>
              <Divider sx={{ my: 1 }} />
              <Box sx={{ px: 2, pb: 0.5 }}>
                <Typography variant="caption" color="text.secondary" fontWeight={600} textTransform="uppercase">
                  Assessments
                </Typography>
              </Box>
            </>
          )}

          {course.mock_test && (
            <ListItem disablePadding>
              <ListItemButton
                disabled={!allModulesComplete}
                onClick={() => setView({ kind: 'test', test: course.mock_test! })}
                sx={{ py: 0.75 }}
              >
                <ListItemIcon sx={{ minWidth: 32 }}>
                  {mockPassed
                    ? <CheckCircle color="success" fontSize="small" />
                    : <Quiz color={allModulesComplete ? 'warning' : 'disabled'} fontSize="small" />}
                </ListItemIcon>
                <ListItemText
                  primary="Practice Quiz"
                  secondary={
                    mockPassed
                      ? `Passed ${Math.round(testResults['mock'].percentage)}%`
                      : !allModulesComplete
                        ? 'Complete all modules first'
                        : `${course.mock_test.questions.length} questions · ${course.mock_test.time_limit} min`
                  }
                  primaryTypographyProps={{ variant: 'body2' }}
                  secondaryTypographyProps={{ variant: 'caption', color: mockPassed ? 'success.main' : 'text.secondary' }}
                />
              </ListItemButton>
            </ListItem>
          )}

          {course.final_test && (
            <ListItem disablePadding>
              <ListItemButton
                disabled={!finalEnabled && !finalPassed}
                onClick={() => {
                  if (finalPassed) setView({ kind: 'certificate' });
                  else if (finalEnabled) setView({ kind: 'test', test: course.final_test! });
                }}
                sx={{ py: 0.75 }}
              >
                <ListItemIcon sx={{ minWidth: 32 }}>
                  {finalPassed
                    ? <CheckCircle color="success" fontSize="small" />
                    : finalEnabled
                      ? <EmojiEvents color="success" fontSize="small" />
                      : <Lock color="disabled" fontSize="small" />}
                </ListItemIcon>
                <ListItemText
                  primary="Final Certification Exam"
                  secondary={
                    finalPassed
                      ? `Passed ${Math.round(testResults['final'].percentage)}%`
                      : !finalEnabled
                        ? 'Pass the Practice Quiz first'
                        : `${course.final_test.questions.length} questions · ${course.final_test.time_limit} min`
                  }
                  primaryTypographyProps={{ variant: 'body2' }}
                  secondaryTypographyProps={{ variant: 'caption', color: finalPassed ? 'success.main' : 'text.secondary' }}
                />
              </ListItemButton>
            </ListItem>
          )}
        </List>
      </Box>

      {/* Main content */}
      <Box sx={{ flex: 1, overflow: 'auto', p: 4 }}>
        {view.kind === 'module' && (
          <>
            <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
              <Box flex={1}>
                <Box display="flex" alignItems="center" gap={1} mb={0.5}>
                  {moduleIcon(view.module)}
                  <Chip label={view.module.content_type} size="small" variant="outlined"
                    color={view.module.content_type === 'video' ? 'error' : view.module.content_type === 'multimedia' ? 'secondary' : 'primary'} />
                  <Chip label={`${view.module.duration} min`} size="small" variant="outlined" />
                </Box>
                <Typography variant="h5" fontWeight={700}>{view.module.title}</Typography>
                {view.module.description && (
                  <Typography variant="body2" color="text.secondary" mt={0.5}>{view.module.description}</Typography>
                )}
              </Box>
              {!completedModules.has(view.module.id) ? (
                <Button variant="contained" color="success" startIcon={<CheckCircle />}
                  onClick={() => markModuleComplete(view.module.id)} sx={{ ml: 2, flexShrink: 0 }}>
                  Mark Complete
                </Button>
              ) : (
                <Chip label="Completed" color="success" icon={<CheckCircle />} />
              )}
            </Box>

            <Divider sx={{ mb: 3 }} />
            <ContentViewer module={view.module} />

            <Box mt={4} display="flex" justifyContent="flex-end" gap={2}>
              {!completedModules.has(view.module.id) && (
                <Button variant="outlined" color="success" onClick={() => markModuleComplete(view.module.id)}>
                  Mark Complete & Continue
                </Button>
              )}
              {(() => {
                const idx  = course.modules.findIndex(m => m.id === view.module.id);
                const next = course.modules[idx + 1];
                if (!next) return null;
                return (
                  <Button variant="contained" onClick={() => setView({ kind: 'module', module: next })}>
                    Next Module →
                  </Button>
                );
              })()}
              {/* Show "Take Practice Quiz" button after last module is complete */}
              {allModulesComplete && course.mock_test && !mockPassed &&
                course.modules.findIndex(m => m.id === view.module.id) === course.modules.length - 1 && (
                <Button variant="contained" startIcon={<Quiz />}
                  onClick={() => setView({ kind: 'modules-complete' })}
                  sx={{ background: BRAND_GRADIENT, color: '#fff', '&:hover': { opacity: 0.9 } }}>
                  Take Practice Quiz
                </Button>
              )}
            </Box>
          </>
        )}

        {view.kind === 'none' && (
          <Box textAlign="center" py={8}>
            {finalPassed ? (
              <>
                <Box sx={{ width: 90, height: 90, borderRadius: '50%', background: BRAND_GRADIENT, display: 'flex', alignItems: 'center', justifyContent: 'center', mx: 'auto', mb: 3 }}>
                  <EmojiEvents sx={{ fontSize: 52, color: '#fff' }} />
                </Box>
                <Typography variant="h4" fontWeight={800} gutterBottom>Course Complete!</Typography>
                <Typography color="text.secondary" paragraph>
                  You've completed <strong>{course.title}</strong> with a score of{' '}
                  <strong>{Math.round(testResults['final']?.percentage ?? 0)}%</strong>.
                </Typography>
                <Button variant="contained" size="large" startIcon={<EmojiEvents />}
                  onClick={() => setView({ kind: 'certificate' })}
                  sx={{ background: BRAND_GRADIENT, color: '#fff', fontWeight: 700, px: 4, py: 1.5, borderRadius: 3, '&:hover': { opacity: 0.9 } }}>
                  View Your Certificate
                </Button>
              </>
            ) : (
              <>
                <Typography variant="h5" fontWeight={700} gutterBottom>{course.title}</Typography>
                <Typography color="text.secondary" paragraph maxWidth={600} mx="auto">{course.description}</Typography>
                {course.modules.length > 0 && (
                  <Button variant="contained" size="large" startIcon={<PlayCircle />}
                    onClick={() => setView({ kind: 'module', module: course.modules[0] })}>
                    {completedModules.size > 0 ? 'Continue Learning' : 'Start Learning'}
                  </Button>
                )}
              </>
            )}
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default CourseDetailPage;
